<script lang="ts">
import { defineComponent, ref, computed, watch, onMounted, nextTick } from 'vue'
import * as AutoKana from 'vanilla-autokana'
import { inputTextDefaultProps } from '@/models/inputTextPartial'
import { getRandomNumber } from '@/utilities/math'

export default defineComponent({
  name: 'InputKanji',

  props: {
    ...inputTextDefaultProps,

    /**
     * フォームに付与する id 属性
     * ※ かな情報取得のキーとして必須
     */
    fieldId: {
      type: String,
      default: '',
    },

    /**
     * 「かな」の value
     */
    modelValueKana: {
      type: String,
      default: ''
    },

    /**
     * フォームのフォーカスアウト時に前後ホワイトスペースの trim するかどうか
     */
    hasTrim: {
      type: Boolean,
      default: true,
    },
  },

  emits: [
    'update:modelValue',
    'update:modelValueKana',
  ],

  setup(props, { emit }) {
    // constants
    const KANA_FIELD_ID = `field-kana-${getRandomNumber(1000000)}`

    const localValue = ref('')
    const localValueKana = ref('')
    const kanaFieldEl = ref()

    let autoKana: any = undefined

    /**
     * 漢字フィールドの input 要素に付与する id 属性値
     * @description props で明示的に受け渡されていない場合は自動算出される
     */
    const kanjiFieldId = computed(() => props.fieldId || `field-kanji-${getRandomNumber(1000000)}`)

    watch(() => props.modelValue, (newValue, oldValue) => {
      if (localValue.value !== newValue) localValue.value = newValue

      // 入力内容が全て削除された（空欄になった）場合、「かな」も自動削除される
      if (oldValue && !newValue) {
        localValueKana.value = ''
        setFurigana('')
        emit('update:modelValueKana', '')
      }
    })

    watch(() => props.modelValueKana, (newValue) => {
      if (localValueKana.value !== newValue) {
        localValueKana.value = newValue
        setFurigana(localValueKana.value)
      }
    })

    onMounted(() => {
      localValue.value = props.modelValue
      localValueKana.value = props.modelValueKana

      nextTick(() => {
        autoKana = AutoKana.bind(kanjiFieldId.value, KANA_FIELD_ID)
        setFurigana(localValueKana.value)
      })
    })

    const updateValue = () => {
      localValueKana.value = autoKana.getFurigana()

      emit('update:modelValue', localValue.value)
      emit('update:modelValueKana', localValueKana.value)
    }

    /**
     * autoKana のオブジェクトとふりがなデータを確保している hidden 要素に値を注入
     * @param value 上書きしたいふりがなの値
     */
    const setFurigana = (value: string) => {
      if (!autoKana) return
      if (autoKana.value === value) return
      autoKana.furigana = value

      if (kanaFieldEl.value) {
        // hidden の value 属性に付与
        (kanaFieldEl.value).value = value || ''
      }
    }

    const onBlur = () => {
      if (props.hasTrim) {
        localValue.value = localValue.value.trim()
        localValueKana.value = localValueKana.value.trim()
        emit('update:modelValue', localValue.value)
        emit('update:modelValueKana', localValueKana.value)
      }
    }

    return {
      // constants
      KANA_FIELD_ID,

      // data
      localValue,
      localValueKana,
      kanaFieldEl,

      // computed
      kanjiFieldId,

      // method
      updateValue,
      onBlur,
    }
  }
})
</script>

<template>
  <div class="div">
    <input
      :id="kanjiFieldId"
      v-model="localValue"
      class="input"
      type="text"
      :name="name ? name : undefined"
      :placeholder="placeholder"
      :disabled="isDisabled"
      :autocomplete="autocomplete"
      :data-is-error="isError"
      @input="updateValue"
      @blur="onBlur">

    <input
      :id="KANA_FIELD_ID"
      ref="kanaFieldEl"
      class="input"
      type="hidden">
  </div>
</template>
